import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { useMemo, useState } from 'react';
import { BookingStatus, ProductType } from '../../../__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  Alert,
  Badge,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Tabs,
} from '../../components/ui';
import {
  useMyStayReservationsQuery,
  useMyStayTripsQuery,
  type IStayBookingFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { formatMoney } from '../StayGuestPage/stayQuoteLines';
import { bookingSubtitle } from './StayBookingComponents';
import {
  sortForGroup,
  stayStatusView,
  stayTripGroup,
  timeLeftLabel,
  type StayTripGroup,
} from './stayBooking';

const Row = styled.button`
  ${tw`flex w-full gap-4 p-3 text-left cursor-pointer items-center`}
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  &:hover {
    box-shadow: var(--shadow-card-hover);
  }
`;

const Photo = styled.img`
  ${tw`object-cover`}
  width: 96px;
  height: 80px;
  border-radius: 8px;
  flex-shrink: 0;
  background: var(--color-bg-secondary);
`;

const Body = styled.div`
  ${tw`flex flex-col flex-1 gap-1`}
  min-width: 0;
`;

const Side = styled.div`
  ${tw`flex flex-col items-end gap-1`}
  flex-shrink: 0;
`;

type Viewer = 'guest' | 'host';

const TAB_LABELS: Record<Viewer, Record<StayTripGroup, string>> = {
  guest: { upcoming: 'Upcoming', requests: 'Requests', past: 'Past', cancelled: 'Cancelled' },
  host: { requests: 'Requests', upcoming: 'Upcoming', past: 'Past', cancelled: 'Cancelled' },
};

export function BookingRow({
  booking,
  viewer,
  onOpen,
}: {
  booking: IStayBookingFieldsFragment;
  viewer: Viewer;
  onOpen: () => void;
}) {
  const view = stayStatusView(booking, viewer);
  const left =
    booking.status === BookingStatus.Requested ? timeLeftLabel(booking.requestExpiresAt) : null;
  const who =
    viewer === 'host'
      ? booking.guest?.firstName || booking.guest?.displayName || 'Guest'
      : booking.car?.location?.city || '';
  const image = booking.car?.images?.[0]?.imageUrl;
  return (
    <Row type="button" onClick={onOpen} data-testid="stay-booking-row">
      {image ? <Photo src={toPublicS3ObjectUrl(image)} alt="" /> : <Photo as="div" />}
      <Body>
        <Strong>{booking.car?.name || 'Place to stay'}</Strong>
        <Muted $size="sm">{[who, bookingSubtitle(booking)].filter(Boolean).join(' · ')}</Muted>
        {left ? <Muted $size="sm">{left}</Muted> : null}
      </Body>
      <Side>
        <Badge tone={view.tone} size="sm">
          {view.label}
        </Badge>
        <Muted $size="sm">{formatMoney(Number(booking.receipt.totalPrice ?? 0))}</Muted>
      </Side>
    </Row>
  );
}

function StayBookingList({
  viewer,
  bookings,
  loading,
  error,
}: {
  viewer: Viewer;
  bookings: IStayBookingFieldsFragment[];
  loading: boolean;
  error?: unknown;
}) {
  const navigate = useNavigate();
  const grouped = useMemo(() => {
    const groups: Record<StayTripGroup, IStayBookingFieldsFragment[]> = {
      requests: [],
      upcoming: [],
      past: [],
      cancelled: [],
    };
    for (const booking of bookings) {
      // Car bookings never list on the stays site (brand scope); skip them anyway.
      if (booking.productType !== ProductType.House) continue;
      const group = stayTripGroup(booking);
      if (group) groups[group].push(booking);
    }
    return groups;
  }, [bookings]);
  const order = Object.keys(TAB_LABELS[viewer]) as StayTripGroup[];
  // Guests land on Upcoming, hosts on Requests (the ones that need an answer).
  const [tab, setTab] = useState<StayTripGroup>(order[0]);
  const rows = sortForGroup(tab, grouped[tab]);
  const path = (id: string) =>
    viewer === 'host' ? `/stays/host/booking/${id}` : `/stays/guest/booking/${id}`;

  return (
    <Stack $gap={6}>
      <Tabs
        items={order.map(key => ({
          key,
          label: TAB_LABELS[viewer][key],
          count: key === 'requests' ? grouped.requests.length : undefined,
        }))}
        value={tab}
        onChange={setTab}
      />
      {error && !bookings.length ? (
        <Alert tone="error" title="Could not load">
          {stayErrorMessage(error)}
        </Alert>
      ) : null}
      {loading && !bookings.length ? (
        <LoadingBlock label="Loading" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={faCalendar}
          title={
            viewer === 'guest' && tab === 'upcoming'
              ? 'No trips booked yet'
              : `Nothing under ${TAB_LABELS[viewer][tab].toLowerCase()}`
          }
          description={
            viewer === 'guest'
              ? 'Time to dust off your bags and plan your next stay.'
              : 'Reservations for your places show up here.'
          }
        />
      ) : (
        <Stack $gap={4}>
          {rows.map(booking => (
            <BookingRow
              key={booking.id}
              booking={booking}
              viewer={viewer}
              onOpen={() => navigate(path(booking.id))}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** The guest's stays on the stays site (spec 016 W21), at `/guest/mybookings`. */
export function StayTripsPage() {
  const { data, loading, error } = useMyStayTripsQuery({ fetchPolicy: 'cache-and-network' });
  return (
    <PageContainer width="lg">
      <PageHeader title="Trips" />
      <StayBookingList
        viewer="guest"
        bookings={data?.myBookings ?? []}
        loading={loading}
        error={error}
      />
    </PageContainer>
  );
}

/** The host's reservations on the stays site (spec 016 W19, W21). */
export function StayReservationsPage() {
  const { data, loading, error } = useMyStayReservationsQuery({
    fetchPolicy: 'cache-and-network',
  });
  return (
    <PageContainer width="lg">
      <PageHeader eyebrow="Hosting" title="Reservations" />
      <StayBookingList
        viewer="host"
        bookings={data?.bookingsForMyListings ?? []}
        loading={loading}
        error={error}
      />
    </PageContainer>
  );
}
